import { useEffect, useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { clearAuth } from '../utils/storage';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Stack,
  CircularProgress,
  Pagination,
  Chip,
} from '@mui/material';
import CarCard from '../components/CarCard';
import CarFilterBar from '../components/CarFilterBar';
import axios from 'axios';
import axiosInstance from '../services/axiosInstance';
import { enqueueSnackbar } from 'notistack';

interface HomePageProps {
  mode: 'light' | 'dark';
  setMode: (mode: 'light' | 'dark') => void;
}

const CarListingPage = ({ mode, setMode }: HomePageProps) => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const parseQueryParam = (key: string): number[] => {
    const raw = searchParams.get(key);
    return raw ? raw.split(',').map(Number) : [];
  };

  const vehicleTransmissionTypeIds = parseQueryParam('vehicleTransmissionTypeIds');
  const vehicleFuelTypeIds = parseQueryParam('vehicleFuelTypeIds');
  const vehicleBrandIds = parseQueryParam('vehicleBrandIds');
  const vehicleBodyTypeIds = parseQueryParam('vehicleBodyTypeIds');
  const vehicleClassIds = parseQueryParam('vehicleClassIds');
  const dealerIds = parseQueryParam('dealerIds');

  const statusIdString = searchParams.get('statusId'); // e.g. "VSL,VPB"
  const statusID = statusIdString ? statusIdString.split(',') : [];

  const parsePriceParams = (): { minAskingPrice: number | null; maxAskingPrice: number | null } => {
    const min = searchParams.get('minAskingPrice');
    const max = searchParams.get('maxAskingPrice');
    return {
      minAskingPrice: min ? Number(min) : null,
      maxAskingPrice: max ? Number(max) : null,
    };
  };

  const { minAskingPrice, maxAskingPrice } = parsePriceParams();

  const parseYearParams = (): { minManufacturingYear: number | null; maxManufacturingYear: number | null } => {
    const min = searchParams.get('minManufacturingYear');
    const max = searchParams.get('maxManufacturingYear');
    return {
      minManufacturingYear: min ? Number(min) : null,
      maxManufacturingYear: max ? Number(max) : null,
    };
  };

  const { minManufacturingYear, maxManufacturingYear } = parseYearParams();

  const parseMileageParams = (): { minMileage: number | null; maxMileage: number | null } => {
    const min = searchParams.get('minMileage');
    const max = searchParams.get('maxMileage');
    return {
      minMileage: min ? Number(min) : null,
      maxMileage: max ? Number(max) : null,
    };
  };

  const { minMileage, maxMileage } = parseMileageParams();

  const parseDepreciationParams = (): { minDepreciationValue: number | null; maxDepreciationValue: number | null } => {
    const min = searchParams.get('minDepreciation');
    const max = searchParams.get('maxDepreciation');
    return {
      minDepreciationValue: min ? Number(min) : null,
      maxDepreciationValue: max ? Number(max) : null,
    };
  };

  const { minDepreciationValue, maxDepreciationValue } = parseDepreciationParams();




  // Parse from query string
  const sortKeys = searchParams.get('sortKeys')?.split(',') || [];
  const sortDirs = searchParams.get('sortDirs')?.split(',') as ('asc' | 'desc')[] || [];

  const sortList = sortKeys.map((key, i) => ({
    key,
    direction: sortDirs[i] || 'asc',
  }));





  const [isLoggedIn, setLoggedin] = useState<boolean>(false);

  const navigate = useNavigate();

  const [viewMode, setViewMode] = useState<'grid' | 'list'>('list');
  const [vehicleList, setVehicleList] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const vehicleModel = searchParams.get('vehicleModel') || '';
  const [searchQuery, setSearchQuery] = useState(searchParams.get('searchKeyword') || ''); // ✅ initialize once


  // Pagination
  const itemsPerPage = viewMode === 'grid' ? 12 : 4;

  const [currentPage, setCurrentPage] = useState(1);
  const [racingAd, setRacingAd] = useState<boolean | null>(null);



  // Apply search and sorting
  const filteredList = vehicleList
    .filter(vehicle => {
      const brandModel = `${vehicle.vehicleBrand} ${vehicle.vehicleModel || ''}`.toLowerCase();
      const matchesSearchQuery = brandModel.includes(searchQuery.toLowerCase());
      const matchesVehicleModel =
        !vehicleModel || brandModel.includes(vehicleModel.toLowerCase());

      return matchesSearchQuery && matchesVehicleModel;
    })
    .sort((a, b) => {
      for (const { key, direction } of sortList) {
        let aVal = a[key];
        let bVal = b[key];

        if (typeof aVal === 'string' && !isNaN(Number(aVal))) aVal = Number(aVal);
        if (typeof bVal === 'string' && !isNaN(Number(bVal))) bVal = Number(bVal);

        if (typeof aVal === 'number' && typeof bVal === 'number') {
          if (aVal !== bVal) return direction === 'asc' ? aVal - bVal : bVal - aVal;
          continue;
        }

        if (typeof aVal === 'string' && typeof bVal === 'string') {
          const result = aVal.localeCompare(bVal, undefined, { sensitivity: 'base' });
          if (result !== 0) return direction === 'asc' ? result : -result;
          continue;
        }

        const result = String(aVal ?? '').localeCompare(String(bVal ?? ''), undefined, {
          sensitivity: 'base',
        });
        if (result !== 0) return direction === 'asc' ? result : -result;
      }

      return 0;
    });






  const totalPages = Math.ceil(filteredList.length / itemsPerPage);
  const paginatedList = filteredList.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );


  useEffect(() => {
    const fetchVehicles = async () => {
      setLoading(false);
      try {
        const payload: any = {
          vehicleTransmissionTypeIds,
          vehicleFuelTypeIds,
          vehicleBrandIds,
          vehicleClassIds,
          vehicleBodyTypeIds,
          minAskingPrice,
          maxAskingPrice,
          minManufacturingYear,
          maxManufacturingYear,
          minMileage,
          maxMileage,
          minDepreciationValue,
          maxDepreciationValue,
          statusID,
          dealerIds,
        };

        // Only include racingAd if it's true
        if (racingAd) {
          payload.racingAd = true;
        }


        const response = await axiosInstance.post(
          "/CustomerVehicle/VehicleGetListPublished",
          payload,
          { headers: { "Content-Type": "application/json" } }
        );

        const result = response.data as {
          responseCode: string;
          responseMessage?: string;
          responseData?: { vehicleList?: any[] };
        };

        if (result.responseCode === "00" && result.responseData) {
          setVehicleList(result.responseData.vehicleList || []);
        } else {
          enqueueSnackbar(result.responseMessage || "⚠️ Failed to load vehicle list", {
            variant: "error",
          });
        }
      } catch {
        enqueueSnackbar("❌ Error fetching vehicle list", { variant: "error" });
      } finally {
        setLoading(true);
      }
    };

    fetchVehicles();
  }, [location.search, racingAd]); // ✅ include racingAd as dependency



  useEffect(() => {
    setCurrentPage(1); // Reset page when searching
  }, [searchQuery]);

  useEffect(() => {
    setCurrentPage(1); // Reset page when view mode changes
  }, [viewMode]);



  const handleFilterChange = (key: string, value: string) => {
    const currentParams = new URLSearchParams(location.search);
    const existing = currentParams.get(key);

    let updatedValues: string[] = [];

    if (existing) {
      const values = existing.split(',');
      if (values.includes(value)) {
        // Deselect: remove the value
        updatedValues = values.filter((v) => v !== value);
      } else {
        // Select: add the value
        updatedValues = [...values, value];
      }

      if (updatedValues.length > 0) {
        currentParams.set(key, updatedValues.join(','));
      } else {
        currentParams.delete(key); // Remove key if nothing selected
      }
    } else {
      // No previous value: add the first one
      updatedValues = [value];
      currentParams.set(key, value);
    }

    // Reset pagination
    currentParams.delete('page');

    // Apply new filters
    navigate(`/vehicle-listing?${currentParams.toString()}`);
  };

  const vehicleClassMap: Record<number, string> = {
    2: 'Light Commercial',
    3: 'Heavy Commercial',
  };

  const handleRemoveVehicleClass = (idToRemove: number) => {
    const currentParams = new URLSearchParams(location.search);
    const existing = currentParams.get('vehicleClassIds');

    if (!existing) return;

    const updated = existing
      .split(',')
      .map(Number)
      .filter((id) => id !== idToRemove);

    if (updated.length > 0) {
      currentParams.set('vehicleClassIds', updated.join(','));
    } else {
      currentParams.delete('vehicleClassIds');
    }

    navigate(`/vehicle-listing?${currentParams.toString()}`);
  };


  return (
    <>
      <Header isLoggedIn={isLoggedIn} mode={mode} setMode={setMode} />
      <Box sx={{ px: 4, py: 6 }}>

        <Box display="flex" justifyContent="center">
          <Box sx={{ width: '100%', maxWidth: 1200 }}>
            {vehicleClassIds.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Showing vehicles from:
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {vehicleClassIds.map((id) => (
                    <Box key={id}>
                      <Chip
                        label={vehicleClassMap[id] || `Class ${id}`}
                        onDelete={() => handleRemoveVehicleClass(id)}
                        color="primary"
                        variant="outlined"
                      />
                    </Box>
                  ))}
                </Box>
              </Box>
            )}


            <CarFilterBar
              viewMode={viewMode}
              onToggleViewMode={() =>
                setViewMode(prev => (prev === 'grid' ? 'list' : 'grid'))
              }
              searchQuery={searchQuery} // ✅ now purely controlled
              onSearchChange={setSearchQuery}
              onFilterChange={handleFilterChange}
            />

            <Box mb={2} display="flex" gap={2}>
              {racingAd ? (
                <Chip
                  label="Racing Ads"
                  color="primary"
                  onDelete={() => setRacingAd(null)} // X clears it
                />
              ) : (
                <Chip
                  label="Show Racing Ads only"
                  clickable
                  color="default"
                  onClick={() => setRacingAd(true)}
                />
              )}
            </Box>


            {loading ? (
              viewMode === 'grid' ? (
                <Stack spacing={2} mt={2}>
                  <Grid container spacing={2}>
                    {paginatedList.map((vehicle: any) => (
                      <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={vehicle.vehicleId}>
                        <CarCard vehicle={vehicle} viewMode="grid" />
                      </Grid>
                    ))}
                  </Grid>

                </Stack>
              ) : (
                <Stack spacing={2} mt={2}>
                  {paginatedList.map(vehicle => (
                    <CarCard
                      key={vehicle.vehicleId}
                      vehicle={vehicle}
                      viewMode="list"
                    />
                  ))}
                </Stack>
              )
            ) : (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                minHeight={200}
              >
                <CircularProgress />
              </Box>
            )}

            {loading && filteredList.length > 0 && (
              <Box
                mt={4}
                display="flex"
                flexDirection="column"
                alignItems="center"
              >
                <Typography variant="body2" color="text.secondary" mb={1}>
                  Showing {(currentPage - 1) * itemsPerPage + 1} to{' '}
                  {Math.min(currentPage * itemsPerPage, filteredList.length)} of{' '}
                  {filteredList.length} Vehicle
                </Typography>
                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={(_, page) => setCurrentPage(page)}
                  color="primary"
                />
              </Box>
            )}
          </Box>
        </Box>
      </Box>
      <Footer />
    </>
  );
};

export default CarListingPage;
